import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@material-ui/core';
import VetService from '../../hooks/VetService';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications, validatePasswordPolicy } from '../../utils';
import { validateEmail } from '../../utils';
import { useTranslation } from 'react-i18next';
import { CreateUpdateVetContactModel, VeterinaryContact } from '../../models/vets';
import { LanguageSelect, PasswordTextField } from '../../elements';
import PasswordPolicyChecklist from '../PasswordPolicyChecklist';
import { ApiService } from '../../hooks';
import useFeatures, { FeatureType } from '../../hooks/features';
import { HealthSystemDomainSettingsResultTranslationViewModel } from '../../api/HealthSystemApi';

interface CreateUpdateVetContactModalProps {
  opened: boolean;
  onModalAction: (result: boolean | null) => void;
  mode: 'create' | 'update';
  contact: VeterinaryContact | null;
  veterinaryId: string;
}

const CreateUpdateVetContactModal: React.FC<any> = ({
  opened,
  onModalAction,
  mode,
  contact,
  veterinaryId
}: CreateUpdateVetContactModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { hasFeature } = useFeatures();
  const passwordPolicyEnabled = hasFeature(FeatureType.USE_PASSWORD_POLICY);

  const vetService = new VetService();
  const apiService = new ApiService();

  // contact fields
  const [name, setName] = React.useState(contact?.name || '');
  const [udaNumber, setUdaNumber] = React.useState(contact?.udaNumber || '');
  const [email, setEmail] = React.useState(contact?.email || '');
  const [phone, setPhone] = React.useState(contact?.phone || '');
  const [isPrimary, setIsPrimary] = React.useState(contact?.isPrimary || false);
  const [languageIso, setLanguageIso] = React.useState(contact?.languageIso || 'EN');
  const [password, setPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  // *contact fields

  const [isBusy, setIsBusy] = React.useState(false);
  const [translations, setTranslations] = React.useState<HealthSystemDomainSettingsResultTranslationViewModel[]>([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (contact) {
      setName(contact?.name || '');
      setUdaNumber(contact?.udaNumber || '');
      setEmail(contact?.email || '');
      setPhone(contact?.phone || '');
      setLanguageIso(contact?.languageIso || '');
      setIsPrimary(contact?.isPrimary || false);
    }
  }, [contact]);

  React.useEffect(() => {
    if (!contact) {
      clearFields();
    }

    if (opened && !loaded) {
      loadTranslations();
    }
  }, [opened]);

  const loadTranslations = async () => {
    setIsBusy(true);

    apiService.api.settings
      .settingsGetTranslations()
      .then((result) => {
        setTranslations(result.data);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const closeDialog = (text: string | null) => {
    if (text == null) {
      clearFields();
      onModalAction(null);
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (mode === 'create') {
      handleCreateContact();
    } else if (mode === 'update') {
      handleUpdateUser();
    }

    clearFields();
  };

  const handleCreateContact = () => {
    setIsBusy(true);

    vetService
      .createVetContact(veterinaryId, buildContactModel())
      .then(() => {
        notifications.success(t('f-add-ok'));
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        onModalAction(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleUpdateUser = () => {
    setIsBusy(true);

    vetService
      .updateVetContact(veterinaryId, buildContactModel())
      .then(() => {
        notifications.success(t('f-upd-ok'));
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        onModalAction(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const buildContactModel = () => {
    return {
      id: contact?.id || null,
      password: password || null,
      name,
      udaNumber,
      email,
      phone,
      isPrimary,
      languageIso
    } as CreateUpdateVetContactModel;
  };

  const clearFields = () => {
    setName('');
    setUdaNumber('');
    setEmail('');
    setPhone('');
    setIsPrimary(false);
    setPassword('');
    setPasswordError(false);
    setLanguageIso('EN');
  };

  const validateForm = (): boolean => {
    if (!name) {
      notifications.warning(t('f-val-usr-email'));
      return false;
    }

    if (!email) {
      notifications.warning(t('f-val-usr-email-valid'));
      return false;
    } else {
      if (!validateEmail(email)) {
        notifications.warning(t('f-val-usr-email-valid'));
        return false;
      }
    }

    if (passwordPolicyEnabled && password && !validatePasswordPolicy(password).isValid) {
      notifications.warning(t('f-val-usr-pass-policy'));
      setPasswordError(true);
      return false;
    }
    setPasswordError(false);

    return true;
  };

  const handleIsPrimaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPrimary(event.target.checked);
  };

  return (
    <Dialog
      id="createUpdateVetContactDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      className={classes.dialog}
      maxWidth="xs"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="createUpdateVetContactDialogTitle" className={classes.dialogHeaderFooter}>
        {mode === 'create' ? t('f-vets-con-add') : t('f-vets-con-upd')}
      </DialogTitle>

      <DialogContent>
        <Grid item container direction="column">
          <Grid item>
            <TextField
              label={t('f-usr-name')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-com-uda')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={udaNumber}
              onChange={(e) => {
                setUdaNumber(e.target.value);
              }}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-com-email')}
              size="small"
              fullWidth
              variant="outlined"
              type="email"
              className={classes.formControl}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-com-phone')}
              size="small"
              fullWidth
              variant="outlined"
              type="email"
              className={classes.formControl}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
              }}
            />
          </Grid>

          <Grid item>
            <PasswordTextField
              label={t('f-usr-pass')}
              size="small"
              fullWidth
              variant="outlined"
              sx={{ m: 1, maxWidth: 250 }}
              value={password}
              error={passwordError}
              helperText={mode === 'update' ? t('f-usr-pass-info') : null}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(false);
              }}
            />

            {passwordPolicyEnabled && password && <PasswordPolicyChecklist password={password} />}
          </Grid>

          <Grid item>
            <LanguageSelect
              cssClass={classes.formControl}
              fullWidth
              translations={translations}
              value={loaded ? languageIso : ''}
              onLanguageChange={(lang) => setLanguageIso(lang)}
            />
          </Grid>

          <Grid item className={classes.isPrimarySwitch}>
            <FormControlLabel
              control={<Switch edge="end" color="primary" onChange={handleIsPrimaryChange} checked={isPrimary} />}
              label={t('f-vets-con-primary')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        {isBusy && <CircularProgress />}

        <Button
          onClick={() => closeDialog(null)}
          variant="outlined"
          disabled={isBusy}
          startIcon={<Icon>{'close'}</Icon>}
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={() => closeDialog('ok')}
          id="saveContactBtn"
          disabled={isBusy}
          startIcon={<Icon>{'save'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-com-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUpdateVetContactModal;
